import { useEffect, useRef, useState } from "react";
import { Eye, EyeOff, Film, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg, imgSrc, uploadFile } from "@/lib/api";
import VideoPlayer from "@/components/VideoPlayer";

const EMPTY = { title: "", description: "", video_url: "", thumbnail_url: "", sort_order: 999, is_published: true };
const inputCls = "w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
const labelCls = "mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500";

const titleFromFile = (fileName = "") => fileName
  .replace(/\.[^.]+$/, "")
  .replace(/[-_]+/g, " ")
  .replace(/\s+/g, " ")
  .trim();

const automaticTitle = () => `Video Nawadah ${new Intl.DateTimeFormat("id-ID", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
}).format(new Date())}`;

export default function VideosAdmin() {
  const [videos, setVideos] = useState(null);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState("");
  const videoRef = useRef(null);
  const imageRef = useRef(null);

  const load = () => API.get("/videos/all").then((response) => setVideos(response.data || [])).catch(() => setVideos([]));
  useEffect(() => { load(); }, []);

  const upload = async (event, field) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(field);
    try {
      const url = await uploadFile(file);
      setForm((current) => ({
        ...current,
        [field]: url,
        ...(field === "video_url" && !current.title.trim()
          ? { title: titleFromFile(file.name) || automaticTitle() }
          : {}),
      }));
      toast.success(field === "video_url" ? "Video terupload" : "Thumbnail terupload");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setUploading("");
      event.target.value = "";
    }
  };

  const save = async () => {
    if (!form.video_url.trim()) {
      toast.error("Upload video atau isi link video terlebih dahulu.");
      return;
    }

    try {
      const payload = {
        ...form,
        title: form.title.trim() || automaticTitle(),
        video_url: form.video_url.trim(),
        thumbnail_url: form.thumbnail_url.trim(),
        sort_order: Number(form.sort_order) || 999,
      };
      if (editId) await API.put(`/videos/${editId}`, payload);
      else await API.post("/videos", payload);
      toast.success(editId ? "Video diperbarui" : "Video ditambahkan");
      setForm(null);
      setEditId(null);
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus video ini secara permanen?")) return;
    try {
      await API.delete(`/videos/${id}`);
      toast.success("Video dihapus");
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const togglePublish = async (item) => {
    try {
      await API.put(`/videos/${item.id}`, { is_published: !item.is_published });
      toast.success(item.is_published ? "Video disembunyikan" : "Video dipublish");
      load();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  return (
    <div data-testid="admin-videos">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Kelola Video</h1>
          <p className="text-sm text-slate-500">Upload video atau tempel link YouTube, lalu atur tampilannya di website.</p>
        </div>
        <button onClick={() => { setForm({ ...EMPTY }); setEditId(null); }} className="inline-flex items-center gap-2 rounded-md bg-[#0B1F3A] px-5 py-2.5 text-sm font-bold text-white" data-testid="add-video">
          <Plus className="h-4 w-4" /> Tambah Video
        </button>
      </div>

      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="video-table">
          <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-widest text-slate-400"><th className="px-5 py-3">Video</th><th className="px-5 py-3">Urutan</th><th className="px-5 py-3">Status</th><th className="px-5 py-3" /></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {(videos || []).map((item) => (
              <tr key={item.id} className="hover:bg-slate-50">
                <td className="px-5 py-3"><p className="max-w-md truncate font-semibold">{item.title}</p><p className="max-w-md truncate text-xs text-slate-400">{item.description}</p></td>
                <td className="px-5 py-3 font-mono-accent text-xs">{item.sort_order ?? 999}</td>
                <td className="px-5 py-3"><span className={`rounded-full px-2 py-1 text-[10px] font-bold ${item.is_published ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{item.is_published ? "PUBLISHED" : "DRAFT"}</span></td>
                <td className="whitespace-nowrap px-5 py-3 text-right">
                  <button onClick={() => togglePublish(item)} title={item.is_published ? "Sembunyikan" : "Publish"} className="p-1.5 text-slate-400 hover:text-emerald-600">{item.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                  <button onClick={() => { setForm({ title: item.title || "", description: item.description || "", video_url: item.video_url || "", thumbnail_url: item.thumbnail_url || "", sort_order: item.sort_order ?? 999, is_published: item.is_published !== false }); setEditId(item.id); }} className="p-1.5 text-slate-400 hover:text-[#0B1F3A]"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(item.id)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {videos && videos.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-slate-400">Belum ada video.</td></tr>}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setForm(null)} />
          <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-md border border-slate-200 bg-white p-6">
            <div className="mb-5 flex items-center justify-between"><h2 className="font-bold">{editId ? "Edit Video" : "Tambah Video"}</h2><button onClick={() => setForm(null)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <div className="space-y-4">
              <div><label className={labelCls}>Judul Video (opsional)</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="Dibuat otomatis jika dikosongkan" data-testid="video-title" /><p className="mt-1 text-[10px] text-slate-400">Saat video di-upload, judul akan diambil dari nama berkas dan tetap bisa diubah.</p></div>
              <div><label className={labelCls}>Deskripsi</label><textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} data-testid="video-description" /></div>
              <div>
                <label className={labelCls}>Video *</label>
                <div className="flex items-center gap-2"><input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} className={inputCls} placeholder="Link YouTube / URL video atau upload →" data-testid="video-url" /><button type="button" onClick={() => videoRef.current?.click()} disabled={Boolean(uploading)} className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2.5 text-xs font-bold"><Upload className="h-4 w-4" /> {uploading === "video_url" ? "..." : "Upload"}</button><input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(event) => upload(event, "video_url")} /></div>
                <p className="mt-1 text-[10px] text-slate-400">Format upload: MP4, WebM, atau MOV, maksimal 50MB. Link YouTube juga didukung.</p>
              </div>
              <div>
                <label className={labelCls}>Thumbnail (opsional)</label>
                <div className="flex items-center gap-2"><input value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} className={inputCls} placeholder="URL gambar atau upload →" /><button type="button" onClick={() => imageRef.current?.click()} disabled={Boolean(uploading)} className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-slate-300 px-3 py-2.5 text-xs font-bold"><Upload className="h-4 w-4" /> {uploading === "thumbnail_url" ? "..." : "Upload"}</button><input ref={imageRef} type="file" accept="image/*" className="hidden" onChange={(event) => upload(event, "thumbnail_url")} /></div>
              </div>
              {form.video_url && <div className="aspect-video overflow-hidden rounded-md bg-black"><VideoPlayer url={form.video_url} title={form.title || "Preview video"} poster={form.thumbnail_url ? imgSrc(form.thumbnail_url) : ""} className="h-full w-full object-cover" /></div>}
              <div><label className={labelCls}>Urutan Tampil</label><input type="number" min="1" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className={inputCls} /><p className="mt-1 text-[10px] text-slate-400">Angka lebih kecil tampil lebih dahulu.</p></div>
              <label className="flex cursor-pointer items-center gap-2 text-sm"><input type="checkbox" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="h-4 w-4 accent-[#0B1F3A]" /> Publish (tampil di website)</label>
              <button onClick={save} disabled={!form.video_url.trim() || Boolean(uploading)} className="w-full rounded-md bg-gold px-6 py-3 text-sm font-bold text-[#0B1F3A] disabled:cursor-not-allowed disabled:opacity-50" data-testid="save-video"><Film className="mr-2 inline h-4 w-4" /> {uploading ? "Menunggu upload selesai..." : "Simpan Video"}</button>
              {!form.video_url.trim() && <p className="-mt-2 text-center text-xs font-medium text-amber-700">Upload video atau tempel link video agar tombol simpan aktif.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
